/**
 * Event Dispatcher Hook
 * ─────────────────────
 * Central dispatcher that connects SSE events to the handler registry.
 * Activated once in App.tsx via <EventDispatcher /> — individual pages
 * do NOT need to subscribe to events for state-cache updates.
 *
 * Cross-cutting concerns handled here:
 * - Self-event filtering (config.skipSelfEvents)
 * - Debouncing (config.debounceMs / config.debounceKey)
 * - Error isolation (one handler failure doesn't block others)
 * - 404 retry for eventual consistency on newly created albums
 */

import React, { useEffect, useRef, useCallback } from 'react';
import { useNotification } from '../contexts/NotificationContext';
import { usePhotoCache } from '../contexts/PhotoCacheContext';
import { useFriendship } from '../contexts/FriendshipContext';
import { useUser } from '../contexts/UserContext';
import { ChangeEvent, ChangeEventTypes } from '../services/notificationService';
import { fetchAlbumById } from '../services/api/albums';
import { EVENT_HANDLERS, StateUpdaters, EventHandler } from '../services/eventUpdateHandlers';

/** All event type strings we'll subscribe to. */
const ALL_EVENT_TYPES = Object.values(ChangeEventTypes) as string[];

const delay = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

/**
 * Fetch an album by ID with one retry on 404 (eventual consistency).
 */
const fetchAlbumByIdWithRetry = async (albumId: string) => {
  try {
    return await fetchAlbumById(albumId);
  } catch (err: unknown) {
    const is404 = err instanceof Error && err.message?.includes('404');
    if (is404) {
      await delay(300);
      return await fetchAlbumById(albumId);
    }
    throw err;
  }
};

export function useEventDispatcher(): void {
  const { subscribe } = useNotification();
  const {
    upsertAlbum,
    prependAlbum,
    removeAlbum,
    patchAlbum,
    invalidatePhotoCache,
  } = usePhotoCache();
  const { triggerRefresh: refreshFriends } = useFriendship();
  const { user } = useUser();

  // Stable ref for the current user ID (avoids re-subscribing on user change)
  const userIdRef = useRef(user?.id);
  useEffect(() => { userIdRef.current = user?.id; }, [user?.id]);

  // Debounce timers: debounceKey → timeout handle
  const debounceTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Cleanup debounce timers on unmount
  useEffect(() => {
    const timers = debounceTimers.current;
    return () => {
      timers.forEach(t => clearTimeout(t));
      timers.clear();
    };
  }, []);

  // Build StateUpdaters (stable via useCallback / refs)
  const buildUpdaters = useCallback((): StateUpdaters => ({
    albums: {
      upsert: upsertAlbum,
      prepend: prependAlbum,
      remove: removeAlbum,
      patch: patchAlbum,
    },
    photos: {
      invalidate: (albumId: string) => invalidatePhotoCache(albumId),
      invalidateAll: () => invalidatePhotoCache(),
    },
    friends: {
      refresh: refreshFriends,
    },
    fetch: {
      albumById: fetchAlbumByIdWithRetry,
    },
  }), [upsertAlbum, prependAlbum, removeAlbum, patchAlbum, invalidatePhotoCache, refreshFriends]);

  // Dispatch a single handler, respecting config
  const dispatchHandler = useCallback((
    handler: EventHandler,
    event: ChangeEvent,
    updaters: StateUpdaters,
  ) => {
    const config = handler.config;

    // Self-event filtering
    if (config?.skipSelfEvents && event.actorUserId === userIdRef.current) {
      return;
    }

    // Compute debounce key
    const debounceMs = config?.debounceMs ?? 0;
    if (debounceMs > 0) {
      const keyFn = config?.debounceKey ?? ((e: ChangeEvent) => `${e.type}:${e.albumId ?? ''}`);
      const key = keyFn(event);

      // Cancel previous timer for same key
      const existing = debounceTimers.current.get(key);
      if (existing) clearTimeout(existing);

      const timer = setTimeout(() => {
        debounceTimers.current.delete(key);
        try {
          const result = handler.handle(event, updaters);
          if (result instanceof Promise) {
            result.catch(err => console.error(`[EventDispatcher] Handler error for ${event.type}:`, err));
          }
        } catch (err) {
          console.error(`[EventDispatcher] Handler error for ${event.type}:`, err);
        }
      }, debounceMs);

      debounceTimers.current.set(key, timer);
      return;
    }

    // Immediate execution
    try {
      const result = handler.handle(event, updaters);
      if (result instanceof Promise) {
        result.catch(err => console.error(`[EventDispatcher] Handler error for ${event.type}:`, err));
      }
    } catch (err) {
      console.error(`[EventDispatcher] Handler error for ${event.type}:`, err);
    }
  }, []);

  // Subscribe to all event types and route to handlers
  useEffect(() => {
    const updaters = buildUpdaters();

    const unsubscribes = ALL_EVENT_TYPES.map(eventType =>
      subscribe(eventType, (event: ChangeEvent) => {
        const handlers = EVENT_HANDLERS[event.type];
        if (!handlers || handlers.length === 0) return;

        for (const handler of handlers) {
          dispatchHandler(handler, event, updaters);
        }
      })
    );

    return () => unsubscribes.forEach(u => u());
  }, [subscribe, buildUpdaters, dispatchHandler]);
}

/**
 * Thin component wrapper — place inside the provider tree in App.tsx.
 * Renders nothing; just activates the dispatcher hook.
 */
export const EventDispatcher: React.FC = () => {
  useEventDispatcher();
  return null;
};
